import React, { useCallback, useEffect, useMemo, useState } from 'react';

const STATUS_LABELS = {
  new: 'Új',
  contacted: 'Kapcsolat felvéve',
  waitlist: 'Várólista',
};

const SUBJECT_LABELS = {
  altalanos: 'Általános kérdés',
  felvetel: 'Felvételi kérelem',
  latogatas: 'Látogatás megszervezése',
  szolgaltatasok: 'Szolgáltatások',
  panasz: 'Panasz vagy észrevétel',
  egyeb: 'Egyéb',
};

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('hu-HU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncate(text, max = 120) {
  if (!text) return '—';
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

const AdminInquiriesManager = ({ apiCall, showToast }) => {
  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadInquiries = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const query = params.toString();
      const path = query ? `/inquiries/admin/list?${query}` : '/inquiries/admin/list';
      const data = await apiCall(path);
      setInquiries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load inquiries:', error);
      setInquiries([]);
      showToast('Az érdeklődések betöltése sikertelen.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [apiCall, showToast, statusFilter]);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const newCount = useMemo(
    () => inquiries.filter((item) => item.status === 'new').length,
    [inquiries]
  );

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      const updated = await apiCall(`/inquiries/${id}/status`, 'PATCH', { status });
      setInquiries((prev) => prev.map((item) => (item._id === id ? updated : item)));
      showToast(`Státusz frissítve: ${STATUS_LABELS[status]}`);
    } catch (error) {
      console.error('Failed to update inquiry status:', error);
      showToast(error.message || 'A státusz frissítése sikertelen.', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleDelete = async (inquiry) => {
    const confirmed = window.confirm(
      `Biztosan törli ezt az érdeklődést?\n\n${inquiry.name} (${inquiry.email})`
    );
    if (!confirmed) return;

    setDeletingId(inquiry._id);
    try {
      await apiCall(`/inquiries/${inquiry._id}`, 'DELETE');
      setInquiries((prev) => prev.filter((item) => item._id !== inquiry._id));
      if (expandedId === inquiry._id) setExpandedId(null);
      showToast('Érdeklődés törölve.');
    } catch (error) {
      console.error('Failed to delete inquiry:', error);
      showToast(error.message || 'A törlés sikertelen.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="inquiries-manager">
      <div className="inquiries-manager-header">
        <div>
          <h2>Érdeklődések</h2>
          <p className="inquiries-manager-desc">
            A főoldali visszahívás és az elérhetőségi űrlap beküldései. E-mail helyett itt kezelhetők.
          </p>
        </div>
        {newCount > 0 && (
          <span className="inquiries-new-badge">{newCount} új</span>
        )}
      </div>

      <div className="inquiries-filters">
        <label>
          Státusz
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Összes</option>
            <option value="new">Új</option>
            <option value="contacted">Kapcsolat felvéve</option>
            <option value="waitlist">Várólista</option>
          </select>
        </label>
        <button type="button" className="nav-btn nav-btn--ghost" onClick={loadInquiries} disabled={isLoading}>
          {isLoading ? 'Betöltés…' : 'Frissítés'}
        </button>
      </div>

      {isLoading && inquiries.length === 0 ? (
        <p className="inquiries-empty">Betöltés…</p>
      ) : inquiries.length === 0 ? (
        <p className="inquiries-empty">Még nincs érdeklődés a kiválasztott szűrőkkel.</p>
      ) : (
        <div className="inquiries-table-wrap">
          <table className="inquiries-table">
            <thead>
              <tr>
                <th>Dátum</th>
                <th>Név</th>
                <th>E-mail</th>
                <th>Telefon</th>
                <th>Tárgy / üzenet</th>
                <th>Státusz</th>
                <th aria-label="Műveletek" />
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inquiry) => {
                const isExpanded = expandedId === inquiry._id;
                const subjectLabel = inquiry.subject
                  ? SUBJECT_LABELS[inquiry.subject] || inquiry.subject
                  : null;
                const detailText = inquiry.message || subjectLabel || '—';

                return (
                  <tr key={inquiry._id} className={inquiry.status === 'new' ? 'inquiry-row--new' : ''}>
                    <td>{formatDate(inquiry.createdAt)}</td>
                    <td>{inquiry.name}</td>
                    <td>
                      <a href={`mailto:${inquiry.email}`}>{inquiry.email}</a>
                    </td>
                    <td>
                      {inquiry.phone ? (
                        <a href={`tel:${inquiry.phone.replace(/\s/g, '')}`}>{inquiry.phone}</a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="inquiry-message-cell">
                      {inquiry.source === 'contact-page' && subjectLabel && (
                        <div className="inquiry-subject">{subjectLabel}</div>
                      )}
                      <div className="inquiry-message-preview">
                        {isExpanded ? inquiry.message || '—' : truncate(inquiry.message || subjectLabel, 80)}
                      </div>
                      {(inquiry.message || subjectLabel) && detailText.length > 80 && (
                        <button
                          type="button"
                          className="inquiry-expand-btn"
                          onClick={() => toggleExpanded(inquiry._id)}
                        >
                          {isExpanded ? 'Kevesebb' : 'Több'}
                        </button>
                      )}
                    </td>
                    <td>
                      <select
                        className={`inquiry-status-select inquiry-status-select--${inquiry.status}`}
                        value={inquiry.status}
                        disabled={updatingId === inquiry._id}
                        onChange={(e) => handleStatusChange(inquiry._id, e.target.value)}
                        aria-label="Státusz"
                      >
                        <option value="new">{STATUS_LABELS.new}</option>
                        <option value="contacted">{STATUS_LABELS.contacted}</option>
                        <option value="waitlist">{STATUS_LABELS.waitlist}</option>
                      </select>
                      {inquiry.handledAt && inquiry.status !== 'new' && (
                        <div className="inquiry-handled-meta">
                          {formatDate(inquiry.handledAt)}
                          {inquiry.handledBy?.nickname || inquiry.handledBy?.username
                            ? ` · ${inquiry.handledBy.nickname || inquiry.handledBy.username}`
                            : ''}
                        </div>
                      )}
                    </td>
                    <td className="inquiry-actions-cell">
                      <button
                        type="button"
                        className="inquiry-delete-btn"
                        onClick={() => handleDelete(inquiry)}
                        disabled={deletingId === inquiry._id || updatingId === inquiry._id}
                        aria-label={`Törlés: ${inquiry.name}`}
                        title="Törlés"
                      >
                        {deletingId === inquiry._id ? '…' : '🗑️'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminInquiriesManager;
